import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, isTestMode, signUpWithInviteCode, markWaitlistUserConverted } from '../services/supabase';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string) => Promise<void>;
    signUpWithInvite: (email: string, password: string, inviteCode: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isTestMode) {
            // Create a mock user in test mode
            const mockUser = {
                id: 'test-user-id',
                email: 'test@example.com',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            } as User;
            setUser(mockUser);
            setLoading(false);
            return;
        }

        // Get initial session
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
            setLoading(false);
        });

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                console.log('Auth state change:', event, session?.user?.email);
                setUser(session?.user ?? null);
                setLoading(false);
            }
        );

        return () => subscription.unsubscribe();
    }, []);

    const signIn = async (email: string, password: string) => {
        if (isTestMode) {
            // Mock sign in for test mode
            const mockUser = {
                id: 'test-user-id',
                email: email,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            } as User;
            setUser(mockUser);
            return;
        }
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
    };

    const signUp = async (email: string, password: string) => {
        if (isTestMode) {
            // Mock sign up for test mode
            const mockUser = {
                id: 'test-user-id',
                email: email,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            } as User;
            setUser(mockUser);
            return;
        }

        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;

        // Check if user was on waitlist and mark as converted
        if (data.user) {
            await markWaitlistUserConverted(email, data.user.id, 'email_password');
        }
    };

    const signUpWithInvite = async (email: string, password: string, inviteCode: string) => {
        if (isTestMode) {
            // Mock sign up with invite for test mode
            const mockUser = {
                id: 'test-user-id',
                email: email,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            } as User;
            setUser(mockUser);
            return;
        }

        try {
            const data = await signUpWithInviteCode(email, password, inviteCode);

            // The user will be set automatically through the auth state change listener
            // but we can set it immediately for better UX
            if (data.user) {
                setUser(data.user as User);
            }
        } catch (error) {
            throw error;
        }
    };

    const signInWithGoogle = async () => {
        if (isTestMode) {
            // Mock Google sign in for test mode
            const mockUser = {
                id: 'test-user-id',
                email: 'test@gmail.com',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            } as User;
            setUser(mockUser);
            return;
        }

        // Use the Supabase callback URL which is already authorized
        const redirectUrl = process.env.NODE_ENV === 'development'
            ? 'http://localhost:3000/auth/callback'
            : `${window.location.origin}/auth/callback`;

        console.log('Environment:', process.env.NODE_ENV);
        console.log('Redirecting to:', redirectUrl);

        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: redirectUrl
            }
        });
        if (error) throw error;
    };

    const signOut = async () => {
        if (isTestMode) {
            // Mock sign out for test mode
            setUser(null);
            return;
        }
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    };

    const value = {
        user,
        loading,
        signIn,
        signUp,
        signUpWithInvite,
        signInWithGoogle,
        signOut,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
